/**
 * LocaleProvider (ORCH-191)
 *
 * Purpose: Push the signed-in user's resolved locale and clock from AuthContext into
 *          the module-level store that the plain-function formatters read, and expose
 *          them (plus the matching date-fns locale for the MUI date picker) to
 *          components through `useLocale()`.
 * Logic: The store is invisible to React, so a locale change mid-session would not
 *        re-render cells already on screen. The provider therefore keys its subtree
 *        on `locale|hourCycle` and remounts it when either changes — blunt, but a
 *        change only happens when a person saves their own profile or an admin edits
 *        a country's assignment, and a remount then is cheaper than threading a
 *        locale argument through every formatter call site.
 * Dependencies: AuthContext, utils/locale
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Locale } from 'date-fns';
import { useAuth } from './AuthContext';
import {
  DEFAULT_LOCALE,
  HourCycle,
  canonicalLocale,
  getActiveHourCycle,
  getActiveLocale,
  getLoadedDateFnsLocale,
  loadDateFnsLocale,
  normalizeHourCycle,
  setActiveLocale,
} from '../utils/locale';

export interface LocaleContextValue {
  /** The BCP 47 tag formatting runs in ('de-DE'). */
  locale: string;
  /** The user's chosen clock, or null to follow the locale. */
  hourCycle: HourCycle;
  /** The date-fns locale for `AdapterDateFns`; enUS until the tag's module has loaded. */
  dateFnsLocale: Locale;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useAuth();
  const locale = canonicalLocale(state.user?.locale) ?? DEFAULT_LOCALE;
  const hourCycle = normalizeHourCycle(state.user?.hourCycle);

  // Written during render so the first paint of the keyed subtree already formats
  // in the right locale; idempotent, so re-renders are harmless.
  if (getActiveLocale() !== locale || getActiveHourCycle() !== hourCycle) {
    setActiveLocale(locale, hourCycle);
  }

  const [dateFnsLocale, setDateFnsLocale] = useState<Locale>(() => getLoadedDateFnsLocale(locale));
  useEffect(() => {
    let alive = true;
    setDateFnsLocale(getLoadedDateFnsLocale(locale));
    loadDateFnsLocale(locale).then((loaded) => {
      if (alive) setDateFnsLocale(loaded);
    });
    return () => {
      alive = false;
    };
  }, [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, hourCycle, dateFnsLocale }),
    [locale, hourCycle, dateFnsLocale]
  );

  return (
    <LocaleContext.Provider value={value}>
      <React.Fragment key={`${locale}|${hourCycle ?? ''}`}>{children}</React.Fragment>
    </LocaleContext.Provider>
  );
};

/**
 * The active locale for components. Outside a provider (unit tests, storybook-style
 * renders) it reports the store's current values and the loaded date-fns locale.
 */
export const useLocale = (): LocaleContextValue => {
  const ctx = useContext(LocaleContext);
  if (ctx) return ctx;
  const locale = getActiveLocale();
  return { locale, hourCycle: getActiveHourCycle(), dateFnsLocale: getLoadedDateFnsLocale(locale) };
};
